import { useCallback, useMemo, useRef, useState } from 'react'
import { Optional, Resume, ResumeWorkExperience } from 'packages/types'
import { useDispatch } from 'react-redux'
import { useUpdateSimpleField } from 'builder/hooks/useUpdateSimpleField'
import { SectionNames, actions } from 'builder/modules/resumeEditor'
import { convertHtmlToText } from 'builder/utils/convertHtmlToText'

function countNewItems(oldItems: string[], newItems: string[]) {
  let newList = 0
  for (let i = 0; i < newItems.length; i++) {
    if (!oldItems.includes(newItems[i])) {
      newList++
    }
  }
  return newList
}

export const useEditContainer = (resumeOld: Resume | null, resumeTailored: Resume | null) => {
  const { updateSimpleField } = useUpdateSimpleField()
  const dispatch = useDispatch()
  const [openWorExperienceCardId, setOpenWorExperienceCardId] =
    useState<Optional<string | number>>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const {
    profile: profileSummary,
    skills: skillsOld = [],
    position: jobTitleOld,
    workExperiences: workExperiencesOld = [],
  } = resumeOld ?? {}
  const {
    profile: profileSummaryTailored,
    locale,
    skills: skillsTailored = [],
    position: jobTitleTailored,
    workExperiences: workExperiencesTailored = [],
  } = resumeTailored ?? {}

  const profileSummaryOld = convertHtmlToText(profileSummary || '')

  const isJobTitleChanged = String(jobTitleOld).trim() !== String(jobTitleTailored).trim()

  const isProfileChanged =
    String(profileSummaryOld).trim() !== String(profileSummaryTailored).trim()

  const countSkillChanges = useMemo(() => {
    const oldSkills = skillsOld.map(skill => skill.skill?.trim()).filter(Boolean) as string[]
    const newSkills = skillsTailored.map(skill => skill.skill?.trim()).filter(Boolean) as string[]
    return countNewItems(oldSkills, newSkills)
  }, [skillsOld, skillsTailored])

  const countWorkExperienceChanges = useMemo(() => {
    const createExperienceStr = (item: ResumeWorkExperience) =>
      [item.title, item.description, item.employer, item.city].join('|')

    const oldWorkExperiences = workExperiencesOld
      .map(createExperienceStr)
      .filter(Boolean) as string[]
    const newWorkExperiencesTailored = workExperiencesTailored
      .map(createExperienceStr)
      .filter(Boolean) as string[]
    return countNewItems(oldWorkExperiences, newWorkExperiencesTailored)
  }, [workExperiencesOld, workExperiencesTailored])

  const isWorkExperienceChanged = useMemo(() => {
    if (workExperiencesOld.length !== workExperiencesTailored.length) return true

    return workExperiencesOld.some((item, index) => {
      return (
        item.title !== workExperiencesTailored[index]?.title ||
        item.description !== workExperiencesTailored[index]?.description ||
        item.employer !== workExperiencesTailored[index]?.employer ||
        item.city !== workExperiencesTailored[index]?.city
      )
    })
  }, [workExperiencesOld, workExperiencesTailored])

  const isSkillsChanged = useMemo(() => {
    if (skillsOld.length !== skillsTailored.length) return true

    return skillsOld.some((item, index) => item.skill !== skillsTailored[index]?.skill)
  }, [skillsOld, skillsTailored])

  const handleProfileSummaryChange = useCallback(
    (value: string) => updateSimpleField({ name: 'profile', value, debounce: true }),
    [updateSimpleField],
  )

  const handleJobTitleChange = useCallback(
    (value: string) => updateSimpleField({ name: 'position', value, debounce: true }),
    [updateSimpleField],
  )

  const handleDeleteSkill = useCallback(
    (cardId: Optional<string | number>) => {
      if (!cardId) return

      dispatch(
        actions.deleteCard({
          sectionName: SectionNames.skills,
          cardId,
        }),
      )
    },
    [dispatch],
  )

  const handleWorkExperienceChange = useCallback(
    (description: string, cardId: Optional<number> | string) => {
      dispatch(
        actions.updateCard({
          sectionId: SectionNames.workExperiences,
          cardId,
          values: { description },
        }),
      )
    },
    [dispatch],
  )

  const handleWorkExperienceToggle = useCallback(
    (cardId: Optional<string | number>) => {
      if (cardId === openWorExperienceCardId) {
        setOpenWorExperienceCardId(null)
      } else {
        setOpenWorExperienceCardId(cardId)
      }
    },
    [openWorExperienceCardId],
  )

  return {
    profileSummaryOld,
    profileSummaryTailored,
    skillsOld,
    skillsTailored,
    locale,
    jobTitleOld,
    jobTitleTailored,
    workExperiencesOld,
    workExperiencesTailored,
    openWorExperienceCardId,
    updateSimpleField,
    handleProfileSummaryChange,
    handleDeleteSkill,
    handleJobTitleChange,
    handleWorkExperienceChange,
    handleWorkExperienceToggle,
    isJobTitleChanged,
    isProfileChanged,
    isWorkExperienceChanged,
    isSkillsChanged,
    scrollRef,
    countSkillChanges,
    countWorkExperienceChanges,
  }
}
